/* eslint-disable react-refresh/only-export-components */
/**
 * 路由配置模块
 *
 * 路由划分：
 * - Platform（无业务线前缀）: 系统管理、欢迎页、错误页
 * - Business（/{bizLine} 前缀）: 业务功能页，按业务线隔离
 */
import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate, useParams } from 'react-router-dom'

import ProtectedRoute from './ProtectedRoute'
import AdminLayout from '@/components/layout/AdminLayout'
import { useBizLineStore } from '@/stores/biz-line-store'
import LoginPage from '@/pages/platform/login/LoginPage'
import PageLoading from '@/components/common/PageLoading'

// ============================================
// 平台页面（跨业务线）
// ============================================

const WelcomePage = lazy(() => import('@/pages/platform/welcome/WelcomePage'))

// 系统管理
const SystemUserPage = lazy(() => import('@/pages/platform/system/user/UserPage'))
const SystemRolePage = lazy(() => import('@/pages/platform/system/role/RolePage'))
const SystemMenuPage = lazy(() => import('@/pages/platform/system/menu/MenuPage'))
const BannerPage = lazy(() => import('@/pages/platform/system/banner/BannerPage'))
const ActivityPage = lazy(() => import('@/pages/platform/system/activity/ActivityPage'))
const OperationLogPage = lazy(() => import('@/pages/platform/system/operation-log/OperationLogPage'))
const AppVersionPage = lazy(() => import('@/pages/platform/system/app-version/AppVersionPage'))

// 错误页面
const ForbiddenPage = lazy(() => import('@/pages/platform/error/ForbiddenPage'))
const NotFoundPage = lazy(() => import('@/pages/platform/error/NotFoundPage'))
const ServerErrorPage = lazy(() => import('@/pages/platform/error/ServerErrorPage'))

// ============================================
// affiliate 业务页面
// ============================================

const ProductManagePage = lazy(() => import('@/pages/affiliate/product/manage/ProductManagePage'))
const ProductCategoryPage = lazy(() => import('@/pages/affiliate/product/category/ProductCategoryPage'))

const OrderMaterialPage = lazy(() => import('@/pages/affiliate/order/material/OrderMaterialPage'))
const OrderConversionPage = lazy(() => import('@/pages/affiliate/order/conversion/OrderConversionPage'))
const ReferralCommissionPage = lazy(() => import('@/pages/affiliate/order/referral-commissions/ReferralCommissionPage'))
const ChannelRelationPage = lazy(() => import('@/pages/affiliate/order/relation/ChannelRelationPage'))
const SitePage = lazy(() => import('@/pages/affiliate/site/SitePage'))
const PositionPage = lazy(() => import('@/pages/affiliate/position/PositionPage'))

// ============================================
// ads 业务页面
// ============================================

const AdsTenantPage = lazy(() => import('@/pages/ads/tenant/TenantPage'))
const AdsDictPage = lazy(() => import('@/pages/ads/dict/HookDictPage'))
const AdsPlatformPage = lazy(() => import('@/pages/ads/platform/PlatformPage'))
const AdsCompliancePage = lazy(() => import('@/pages/ads/compliance/ComplianceRulePage'))
const AdsTenantReportPage = lazy(() => import('@/pages/ads/report/TenantReportPage'))

// ============================================
// cas 业务页面
// ============================================

const CasTenantPage = lazy(() => import('@/pages/cas/tenant/TenantPage'))

const FinanceAccountPage = lazy(() => import('@/pages/affiliate/finance/account/AccountPage'))
const FinanceDetailPage = lazy(() => import('@/pages/affiliate/finance/detail/DetailPage'))

const TransactionRecordPage = lazy(() => import('@/pages/affiliate/transaction/record/RecordPage'))

const CustomerBankCardPage = lazy(() => import('@/pages/affiliate/customer/bank-card/BankCardPage'))
const CustomerReferralPage = lazy(() => import('@/pages/affiliate/customer/referral/ReferralPage'))
const AffiliateUserPage = lazy(() => import('@/pages/affiliate/customer/affiliate-user/AffiliateUserPage'))
const KycPage = lazy(() => import('@/pages/affiliate/customer/kyc/KycPage'))
const TeamDetailPage = lazy(() => import('@/pages/affiliate/customer/team-detail/TeamDetailPage'))
const ActiveLogDetailPage = lazy(() => import('@/pages/affiliate/customer/active-log-detail/ActiveLogDetailPage'))
const UserApplyPage = lazy(() => import('@/pages/affiliate/customer/user-apply/UserApplyPage'))

// ============================================
// 懒加载包装器
// ============================================

const LazyPage = ({ Component }: { Component: React.LazyExoticComponent<() => React.ReactElement> }) => (
  <Suspense fallback={<PageLoading />}>
    <Component />
  </Suspense>
)

/**
 * 业务路由重定向（注入当前 bizLine）
 */
function BizRedirect({ to }: { to: string }) {
  const { bizLine } = useParams<{ bizLine: string }>()
  return <Navigate to={`/${bizLine}${to}`} replace />
}

/**
 * /welcome 重定向：按当前业务线状态跳到对应业务线欢迎页
 */
function WelcomeRedirect() {
  const bizLine = useBizLineStore((s) => s.bizLine)
  return <Navigate to={`/${bizLine}/welcome`} replace />
}

// ============================================
// 路由配置
// ============================================

const basename = import.meta.env.BASE_URL?.replace(/\/$/, '') || ''

export const router = createBrowserRouter([
  // 根路径重定向到 /welcome
  {
    path: '/',
    element: <Navigate to="/welcome" replace />,
  },

  // 登录页（公开）
  {
    path: '/login',
    element: <LoginPage />,
  },

  // 错误页面（公开）
  {
    path: '/error/403',
    element: <LazyPage Component={ForbiddenPage} />,
  },
  {
    path: '/error/404',
    element: <LazyPage Component={NotFoundPage} />,
  },
  {
    path: '/error/500',
    element: <LazyPage Component={ServerErrorPage} />,
  },

  // 受保护的路由
  {
    element: (
      <ProtectedRoute>
        <AdminLayout />
      </ProtectedRoute>
    ),
    children: [
      // ==================== Platform（无 bizLine 前缀） ====================

      // 欢迎页（重定向到当前业务线欢迎页 /{bizLine}/welcome）
      {
        path: '/welcome',
        element: <WelcomeRedirect />,
      },

      // 系统管理
      {
        path: '/system',
        children: [
          { index: true, element: <Navigate to="/system/user" replace /> },
          { path: 'user', element: <LazyPage Component={SystemUserPage} /> },
          { path: 'role', element: <LazyPage Component={SystemRolePage} /> },
          { path: 'menu', element: <LazyPage Component={SystemMenuPage} /> },
          { path: 'banner', element: <LazyPage Component={BannerPage} /> },
          { path: 'activity', element: <LazyPage Component={ActivityPage} /> },
          { path: 'operation-log', element: <LazyPage Component={OperationLogPage} /> },
          { path: 'app-version', element: <LazyPage Component={AppVersionPage} /> },
        ],
      },

      // ==================== Business（/{bizLine} 前缀） ====================

      // cas 管理（静态路径，优先于 /:bizLine 通配；菜单 path 与 a_admin_menu biz_line='cas' 一致）
      { path: '/cas/tenant', element: <LazyPage Component={CasTenantPage} /> },

      {
        path: '/:bizLine',
        children: [
          // 业务线欢迎页（affiliate/ads/cas 各自的工作台）
          { path: 'welcome', element: <LazyPage Component={WelcomePage} /> },

          // 商品管理
          { path: 'product', element: <BizRedirect to="/product/manage" /> },
          { path: 'product/manage', element: <LazyPage Component={ProductManagePage} /> },
          { path: 'product/category', element: <LazyPage Component={ProductCategoryPage} /> },

          // 推广管理
          { path: 'order', element: <BizRedirect to="/order/relation" /> },
          { path: 'order/relation', element: <LazyPage Component={ChannelRelationPage} /> },
          { path: 'order/site', element: <LazyPage Component={SitePage} /> },
          { path: 'order/position', element: <LazyPage Component={PositionPage} /> },
          { path: 'order/material', element: <LazyPage Component={OrderMaterialPage} /> },
          { path: 'order/conversion', element: <LazyPage Component={OrderConversionPage} /> },
          { path: 'order/referral-commissions', element: <LazyPage Component={ReferralCommissionPage} /> },

          // 财务管理
          { path: 'finance', element: <BizRedirect to="/finance/account" /> },
          { path: 'finance/account', element: <LazyPage Component={FinanceAccountPage} /> },
          { path: 'finance/detail', element: <LazyPage Component={FinanceDetailPage} /> },

          // 交易管理
          { path: 'transaction', element: <BizRedirect to="/transaction/record" /> },
          { path: 'transaction/record', element: <LazyPage Component={TransactionRecordPage} /> },

          // 客户管理
          { path: 'customer', element: <BizRedirect to="/customer/affiliate-user" /> },
          { path: 'customer/affiliate-user', element: <LazyPage Component={AffiliateUserPage} /> },
          { path: 'customer/bank-card', element: <LazyPage Component={CustomerBankCardPage} /> },
          { path: 'customer/referral', element: <LazyPage Component={CustomerReferralPage} /> },
          { path: 'customer/kyc', element: <LazyPage Component={KycPage} /> },
          { path: 'customer/team-list', element: <LazyPage Component={TeamDetailPage} /> },
          { path: 'customer/active-log-list', element: <LazyPage Component={ActiveLogDetailPage} /> },
          { path: 'customer/user-apply', element: <LazyPage Component={UserApplyPage} /> },

          // 独立业务页面
          { path: 'site', element: <LazyPage Component={SitePage} /> },
          { path: 'position', element: <LazyPage Component={PositionPage} /> },

          // ads 管理（已嵌套在 /:bizLine 下，子路径不再重复 ads/ 前缀）
          { path: 'ads', element: <BizRedirect to="/tenant" /> },
          { path: 'tenant', element: <LazyPage Component={AdsTenantPage} /> },
          { path: 'dict', element: <LazyPage Component={AdsDictPage} /> },
          { path: 'platform', element: <LazyPage Component={AdsPlatformPage} /> },
          { path: 'compliance', element: <LazyPage Component={AdsCompliancePage} /> },
          { path: 'report', element: <LazyPage Component={AdsTenantReportPage} /> },
        ],
      },
    ],
  },

  // 404 页面
  {
    path: '*',
    element: <LazyPage Component={NotFoundPage} />,
  },
], { basename })

export default router
